/*
 * Luxe header: announcement slider, desktop mega menu, mobile drawer, search
 * bar and country selector. Search results reuse the theme's
 * search-predictive-grid section; the cart stays the theme's own
 * <cart-notification> element (theme.js).
 */
(() => {
  if (window.customElements.get('sticky-header')) return;

  const DESKTOP_QUERY = '(min-width: 990px)';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const isVisible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length) && getComputedStyle(el).visibility !== 'hidden';

  // Shared by the mobile drawer and the search bar.
  const lockScroll = () => {
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--luxe-scrollbar', `${scrollbar}px`);
    document.documentElement.classList.add('luxe-scroll-lock');
  };
  const unlockScroll = () => {
    document.documentElement.classList.remove('luxe-scroll-lock');
    document.documentElement.style.removeProperty('--luxe-scrollbar');
  };

  /* ------------------------------------------------------------------ */
  /* Announcement bar                                                   */
  /* ------------------------------------------------------------------ */
  class LuxeAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.speed = Math.max(2, parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.paused = false;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));

      // Pause while the pointer or keyboard focus is inside the bar.
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.resume();
      });

      this.onBlockSelect = (e) => {
        const i = this.slides.findIndex((s) => s.dataset.blockId === e.detail.blockId);
        if (i < 0) return;
        this.go(i);
        this.pause();
      };
      this.onBlockDeselect = () => this.resume();
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.go(this.index);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    go(i, userInitiated = false) {
      if (!this.slides.length) return;
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, n) => {
        const active = n === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.toggleAttribute('inert', !active);
      });
      if (userInitiated) this.start();
    }

    start() {
      this.stop();
      if (!this.autoplay || this.paused) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    stop() {
      clearInterval(this.timer);
    }

    pause() {
      this.paused = true;
      this.stop();
    }

    resume() {
      this.paused = false;
      this.start();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Country / currency selector (Shopify localization form)            */
  /* ------------------------------------------------------------------ */
  class LuxeLocalization extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-localization-toggle]');
      this.list = this.querySelector('[data-localization-list]');
      if (!this.button || !this.list) return;

      // Options are native submit buttons (name="country_code"), no JS needed to submit.
      this.button.addEventListener('click', () => this.toggle());
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.toggle(false);
      });
      this.onDocClick = (e) => {
        if (!this.contains(e.target)) this.toggle(false);
      };
      document.addEventListener('click', this.onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    get options() {
      return Array.from(this.list.querySelectorAll('[data-value]'));
    }

    toggle(force) {
      const open = typeof force === 'boolean' ? force : this.button.getAttribute('aria-expanded') !== 'true';
      this.button.setAttribute('aria-expanded', String(open));
      this.list.hidden = !open;
      if (open) {
        const current = this.list.querySelector('[aria-current="true"]') || this.options[0];
        current?.scrollIntoView({ block: 'nearest' });
      }
    }

    onKeydown(e) {
      const open = this.button.getAttribute('aria-expanded') === 'true';
      if (e.key === 'Escape' && open) {
        e.stopPropagation();
        this.toggle(false);
        this.button.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      if (!open) this.toggle(true);
      const options = this.options;
      const i = options.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? Math.min(i + 1, options.length - 1) : Math.max(i - 1, 0);
      options[i === -1 ? 0 : next]?.focus();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Mobile drawer                                                      */
  /* ------------------------------------------------------------------ */
  class LuxeMenuDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.luxe-drawer__panel');
      this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-accordion-toggle]').forEach((btn) =>
        btn.addEventListener('click', () => this.toggleAccordion(btn))
      );
      this.addEventListener('keydown', (e) => this.onKeydown(e));

      this.mql = window.matchMedia(DESKTOP_QUERY);
      this.onBreakpoint = () => this.mql.matches && this.close(false);
      this.mql.addEventListener('change', this.onBreakpoint);
    }

    disconnectedCallback() {
      this.mql?.removeEventListener('change', this.onBreakpoint);
      if (this.classList.contains('is-open')) this.unlockScroll();
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      this.opener?.setAttribute('aria-expanded', 'true');
      this.lockScroll();
      this.dispatchEvent(new CustomEvent('luxe:drawer', { bubbles: true, detail: { open: true } }));
      requestAnimationFrame(() => this.querySelector('[data-drawer-close].luxe-drawer__close')?.focus());
    }

    close(returnFocus = true) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
      this.opener?.setAttribute('aria-expanded', 'false');
      this.unlockScroll();
      this.dispatchEvent(new CustomEvent('luxe:drawer', { bubbles: true, detail: { open: false } }));
      if (returnFocus) this.opener?.focus();
    }

    lockScroll() {
      lockScroll();
    }

    unlockScroll() {
      unlockScroll();
    }

    toggleAccordion(btn) {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      document.getElementById(btn.getAttribute('aria-controls'))?.classList.toggle('is-open', !expanded);
    }

    onKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        // An open country list stops propagation, so this only runs when it is closed.
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      // Focus trap: keep Tab within the visible, focusable parts of the panel.
      const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(isVisible);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* <luxe-search>: full-width bar under the header + predictive results */
  /* ------------------------------------------------------------------ */
  const PREDICTIVE_SECTION = 'search-predictive-grid';
  const PREDICTIVE_DELAY = 300;

  class LuxeSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.resultsContent = this.querySelector('[data-search-results-content]');
      this.status = this.querySelector('[data-search-status]');
      this.cache = new Map();

      this.querySelectorAll('[data-search-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.form.addEventListener('submit', (e) => {
        if (!this.input.value.trim()) e.preventDefault();
      });
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), PREDICTIVE_DELAY);
      });
      this.onDocKeydown = (e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        this.close();
      };
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      document.removeEventListener('keydown', this.onDocKeydown);
      if (this.isOpen) unlockScroll();
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    get triggers() {
      return Array.from(document.querySelectorAll(`[data-search-open][aria-controls="${this.id}"]`));
    }

    toggle(opener) {
      if (this.isOpen) this.close();
      else this.open(opener);
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      lockScroll();
      document.addEventListener('keydown', this.onDocKeydown);
      this.dispatchEvent(new CustomEvent('luxe:search', { bubbles: true, detail: { open: true } }));
      // Synchronous, still inside the click, so mobile browsers show the keyboard.
      this.input.focus({ preventScroll: true });
    }

    close(returnFocus = true) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
      this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      unlockScroll();
      document.removeEventListener('keydown', this.onDocKeydown);
      this.dispatchEvent(new CustomEvent('luxe:search', { bubbles: true, detail: { open: false } }));
      if (!returnFocus) return;
      // The opener may have been hidden by a breakpoint change; fall back to the visible trigger.
      const target = this.opener && isVisible(this.opener) ? this.opener : this.triggers.find(isVisible);
      target?.focus();
    }

    search() {
      const query = this.input.value.trim();
      const key = query.toLowerCase();
      this.controller?.abort();
      if (!query) {
        this.render('');
        return;
      }
      if (this.cache.has(key)) {
        this.render(this.cache.get(key));
        return;
      }

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'query,product,collection,page,article',
        'resources[options][fields]': 'title,tag,vendor,product_type,variants.title,variants.sku',
        'resources[options][prefix]': 'last',
        'resources[options][unavailable_products]': 'last',
        section_id: PREDICTIVE_SECTION,
      });
      const controller = new AbortController();
      this.controller = controller;
      this.classList.add('is-loading');

      fetch(`${this.dataset.predictiveUrl}?${params}`, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const section = new DOMParser()
            .parseFromString(text, 'text/html')
            .getElementById(`shopify-section-${PREDICTIVE_SECTION}`);
          const markup = section ? section.innerHTML : '';
          this.cache.set(key, markup);
          this.render(markup);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') this.render('');
        })
        .finally(() => {
          if (this.controller === controller) this.classList.remove('is-loading');
        });
    }

    render(markup) {
      this.resultsContent.innerHTML = markup;
      this.results.hidden = !markup.trim();
      // Announce the results heading ("Search for …") or the empty-state message.
      const summary = this.resultsContent.querySelector('#search-results-empty p, .search-list-item .fs-18');
      this.status.textContent = summary ? summary.textContent.trim() : '';
    }
  }


  /* ------------------------------------------------------------------ */
  /* <mega-menu>: desktop dropdowns (mega, simple dropdown, plain link)  */
  /* ------------------------------------------------------------------ */
  const OPEN_DELAY = 100;
  const CLOSE_DELAY = 150;

  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-menu-item]'));
      this.tops = this.items.map((item) => item.querySelector('[data-top]'));
      this.mql = window.matchMedia(DESKTOP_QUERY);
      this.current = null;
      this.editorLock = false;

      this.items.forEach((item, i) => this.bindItem(item, i));

      this.onDocClick = (e) => {
        if (!this.current) return;
        if (!this.contains(e.target) || e.target.closest('[data-overlay-close]')) this.close(true);
      };
      this.onDocKeydown = (e) => {
        if (e.key !== 'Escape' || !this.current) return;
        const top = this.current.querySelector('[data-top]');
        this.close(true);
        top.focus();
      };
      this.onBreakpoint = () => this.close(true);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onDocKeydown);
      this.mql.addEventListener('change', this.onBreakpoint);

      // Keep the backdrop height in sync when promo images load or fonts swap.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.syncHeight());
        this.querySelectorAll('.mega-menu__panel').forEach((panel) => this.resizeObserver.observe(panel));
      }

      this.bindEditor();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onDocKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.mql.removeEventListener('change', this.onBreakpoint);
      this.resizeObserver?.disconnect();
      clearTimeout(this.timer);
    }

    bindItem(item, i) {
      const top = this.tops[i];
      const hasPanel = item.dataset.type !== 'link';

      // Hover (mouse only): short delay before a fresh open; switching between
      // items while a panel is already open happens right away.
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !this.mql.matches) return;
        clearTimeout(this.timer);
        if (hasPanel && this.current === item) return;
        if (hasPanel && this.current) {
          this.open(item, 'hover');
          return;
        }
        this.timer = setTimeout(() => (hasPanel ? this.open(item, 'hover') : this.close()), OPEN_DELAY);
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse' || !this.mql.matches) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.close(), CLOSE_DELAY);
      });

      top.addEventListener('keydown', (e) => this.onTopKeydown(e, i));
      if (!hasPanel) return;

      // Click / Enter / Space (the trigger is a <button>).
      top.addEventListener('click', () => {
        if (this.current !== item) {
          this.open(item, 'click');
          return;
        }
        // Don't close a panel that hover opened a moment before the click.
        if (this.openedBy === 'hover' && Date.now() - this.openedAt < 400) return;
        this.close(true);
      });

      // Keyboard users tabbing out of an open item close it.
      item.addEventListener('focusout', (e) => {
        if (this.current === item && e.relatedTarget && !item.contains(e.relatedTarget)) this.close();
      });
    }

    onTopKeydown(e, i) {
      if (!this.mql.matches) return;
      const item = this.items[i];
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowLeft': {
          e.preventDefault();
          const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tops.length) % this.tops.length;
          if (this.current) this.close(true);
          this.tops[next].focus();
          break;
        }
        case 'ArrowDown':
          if (item.dataset.type === 'link') return;
          e.preventDefault();
          this.open(item, 'key');
          requestAnimationFrame(() => item.querySelector('[data-panel]')?.querySelector(FOCUSABLE)?.focus());
          break;
        case 'Home':
        case 'End':
          e.preventDefault();
          this.tops[e.key === 'Home' ? 0 : this.tops.length - 1].focus();
          break;
      }
    }

    open(item, via) {
      // Set by <sticky-header> while the search bar is open, so hover can't open panels over it.
      if (this.suppressed || this.current === item) return;
      const prev = this.current;
      if (prev) this.setItem(prev, false);

      const isMega = item.dataset.type === 'mega';
      // Mega -> mega: keep the backdrop, crossfade panels and animate its height.
      this.classList.toggle('is-switching', !!prev && isMega && prev.dataset.type === 'mega');
      this.current = item;
      this.openedBy = via;
      this.openedAt = Date.now();
      this.setItem(item, true);
      this.syncHeight();
      this.classList.toggle('is-mega-open', isMega);
      if (!prev) this.emit(true);
    }

    close(force = false) {
      clearTimeout(this.timer);
      if (!this.current || (this.editorLock && !force)) return;
      this.setItem(this.current, false);
      this.current = null;
      this.classList.remove('is-mega-open', 'is-switching');
      this.emit(false);
    }

    setItem(item, open) {
      item.classList.toggle('is-open', open);
      item.querySelector('[data-top]').setAttribute('aria-expanded', String(open));
    }

    syncHeight() {
      if (this.current?.dataset.type !== 'mega') return;
      const panel = this.current.querySelector('.mega-menu__panel');
      this.style.setProperty('--mega-height', `${panel.offsetHeight}px`);
    }

    emit(open) {
      this.dispatchEvent(new CustomEvent('mega-menu:toggle', { bubbles: true, detail: { open } }));
    }

    // Theme editor: keep the right panel open while a promo block is selected.
    bindEditor() {
      if (!window.Shopify?.designMode) return;
      this.onBlockSelect = (e) => {
        const block = this.querySelector(`[data-shopify-editor-block="${CSS.escape(e.detail.blockId)}"]`);
        const item = block?.closest('[data-menu-item]');
        if (!item || !this.mql.matches) return;
        this.open(item, 'editor');
        this.editorLock = true;
      };
      this.onBlockDeselect = () => {
        if (!this.editorLock) return;
        this.editorLock = false;
        this.close(true);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  /* ------------------------------------------------------------------ */
  /* <sticky-header>: transparent -> solid state, mobile buttons         */
  /* ------------------------------------------------------------------ */
  const SCROLL_THRESHOLD = 50;
  // Class theme.js puts on <html> while its cart drawer is open.
  const THEME_OPEN_CLASSES = ['open-minicart'];

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('header');
      this.section = this.closest('.shopify-section');
      this.transparent = this.dataset.transparent === 'true';
      this.sticky = this.dataset.sticky === 'true';
      this.state = { scrolled: false, hovered: false, focused: false, menu: false, drawer: false, search: false, themeDrawer: false };

      this.bindState();
      this.bindMobileButtons();
      this.observeHeight();
      this.positionOverlay();

      console.log(
        `[sticky-header] ready: transparent=${this.transparent}, sticky=${this.sticky}, state=${this.classList.contains('header--solid') ? 'solid' : 'transparent'}`
      );
      if (this.dataset.nestedMenu !== 'true') {
        console.info(
          '[sticky-header] The selected menu has no nested links, so no dropdowns or mega menus are shown. Add child links under top-level items in Online Store > Navigation.'
        );
      }
    }

    // Homepage overlay: the section is position: fixed/absolute, so place it
    // directly under whatever header-group sections (announcement bar) sit above it.
    positionOverlay() {
      if (!this.transparent || !this.section) return;
      const above = [];
      let el = this.section.previousElementSibling;
      while (el && el.classList.contains('shopify-section-group-header-group')) {
        above.push(el);
        el = el.previousElementSibling;
      }
      const update = () => {
        const offset = above.reduce((sum, s) => sum + s.offsetHeight, 0);
        const top = this.sticky ? Math.max(0, offset - window.scrollY) : offset;
        this.section.style.setProperty('--luxe-header-top', `${top}px`);
        document.documentElement.style.setProperty('--luxe-header-offset', `${offset}px`);
      };
      this.updateOverlay = update;
      update();
      window.addEventListener('resize', update, { passive: true });
      if ('ResizeObserver' in window && above.length) {
        this.aboveObserver = new ResizeObserver(update);
        above.forEach((s) => this.aboveObserver.observe(s));
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.updateOverlay) window.removeEventListener('resize', this.updateOverlay);
      this.htmlObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.aboveObserver?.disconnect();
    }

    bindState() {
      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.updateOverlay?.();
          this.set('scrolled', window.scrollY > SCROLL_THRESHOLD);
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      const canHover = window.matchMedia('(hover: hover)');
      this.addEventListener('mouseenter', () => canHover.matches && this.set('hovered', true));
      this.addEventListener('mouseleave', () => this.set('hovered', false));
      // Keyboard focus inside the header also makes it solid, so focus rings stay readable.
      this.addEventListener('focusin', (e) => this.set('focused', e.target.matches(':focus-visible')));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.set('focused', false);
      });

      this.addEventListener('mega-menu:toggle', (e) => this.set('menu', e.detail.open));
      this.addEventListener('luxe:drawer', (e) => this.set('drawer', e.detail.open));

      const root = document.documentElement;
      const checkTheme = () => {
        const open = THEME_OPEN_CLASSES.some((c) => root.classList.contains(c));
        // The theme's cart drawer opens above the page: don't leave the search bar behind it.
        if (open) this.querySelector('luxe-search')?.close(false);
        this.set('themeDrawer', open);
      };
      this.htmlObserver = new MutationObserver(checkTheme);
      this.htmlObserver.observe(root, { attributes: true, attributeFilter: ['class'] });

      this.state.scrolled = window.scrollY > SCROLL_THRESHOLD;
      this.state.themeDrawer = THEME_OPEN_CLASSES.some((c) => root.classList.contains(c));
      this.render();
    }

    set(key, value) {
      if (this.state[key] === value) return;
      this.state[key] = value;
      this.render();
    }

    render() {
      const solid = !this.transparent || Object.values(this.state).some(Boolean);
      this.classList.toggle('header--solid', solid);
      this.classList.toggle('header--transparent', !solid);
    }

    bindMobileButtons() {
      const drawer = this.querySelector('luxe-menu-drawer');
      const search = this.querySelector('luxe-search');
      const megaMenu = this.querySelector('mega-menu');
      const burger = this.querySelector('[data-drawer-open]');
      burger?.addEventListener('click', () => {
        // Close search first so its scroll unlock can't undo the drawer's lock.
        search?.close(false);
        drawer?.open(burger);
      });

      // Header search icons (mobile left, desktop right) toggle the search bar.
      this.querySelectorAll('[data-search-open]').forEach((btn) =>
        btn.addEventListener('click', () => search?.toggle(btn))
      );
      this.addEventListener('luxe:search', (e) => {
        this.set('search', e.detail.open);
        if (!megaMenu) return;
        megaMenu.suppressed = e.detail.open;
        if (e.detail.open) megaMenu.close(true);
      });
    }

    observeHeight() {
      const setHeight = () => {
        const h = this.header.offsetHeight;
        this.section?.style.setProperty('--luxe-header-height', `${h}px`);
        document.documentElement.style.setProperty('--luxe-header-height', `${h}px`);
      };
      setHeight();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(setHeight);
        this.resizeObserver.observe(this.header);
      }
    }
  }

  customElements.define('luxe-announcement', LuxeAnnouncement);
  customElements.define('luxe-localization', LuxeLocalization);
  customElements.define('luxe-menu-drawer', LuxeMenuDrawer);
  customElements.define('luxe-search', LuxeSearch);
  customElements.define('mega-menu', MegaMenu);
  customElements.define('sticky-header', StickyHeader);
})();
